import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { useCompany, useCompanies } from "../../hooks/useCompanyData";
import { useProducts } from "../../hooks/useProductData";
import { useScrollReveal } from "../../hooks/useScrollReveal";
import { createSlug, getProductUrl } from "../../lib/slug";
import type { CompanyBasic } from "../../hooks/useCompanyData";
import type { Product } from "../../mocks/products";
import SEO from "@/components/SEO";
import { LocalBusinessSchema, BreadcrumbSchema } from "@/components/StructuredData";

// ── Skeleton ──────────────────────────────────────────────────────────────────
const CompanySkeleton = () => (
  <div className="min-h-screen bg-[#F8F9FA]">
    <Navbar />
    <div className="h-72 bg-gray-200 animate-pulse" />
    <div className="max-w-[1400px] mx-auto px-6 py-16 grid lg:grid-cols-3 gap-8">
      {[1, 2, 3].map((i) => <div key={i} className="h-60 bg-gray-100 rounded-2xl animate-pulse" />)}
    </div>
    <Footer />
  </div>
);

// ── Hero ──────────────────────────────────────────────────────────────────────
const CompanyDetailHero = ({ company, isKr }: { company: CompanyBasic; isKr: boolean }) => {
  const logoStyle = company.logoFrom && company.logoTo
    ? { background: `linear-gradient(135deg, ${company.logoFrom} 0%, ${company.logoTo} 60%, #001428 100%)` }
    : { background: "linear-gradient(135deg, #1C4E6B 0%, #2B7A9C 60%, #001428 100%)" };

  return (
    <section className="relative overflow-hidden" style={logoStyle}>
      <div className="absolute inset-0 opacity-5">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid-co" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-co)" />
        </svg>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 py-10 lg:py-14">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-white/50 text-xs mb-6">
          <Link to="/" className="hover:text-white transition-colors cursor-pointer">{isKr ? "홈" : "Trang chủ"}</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/doanh-nghiep" className="hover:text-white transition-colors cursor-pointer">{isKr ? "한국기업 소개" : "Giới Thiệu Doanh Nghiệp"}</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-white/80">{company.name}</span>
        </div>

        <div className="flex flex-col lg:flex-row items-center lg:items-start gap-10">
          {/* Logo - increased ~70% */}
          <div className="flex-shrink-0">
            <div
              className="w-56 h-56 lg:w-64 lg:h-64 rounded-3xl flex flex-col items-center justify-center shadow-2xl border border-white/20 overflow-hidden"
              style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(10px)" }}
            >
              {company.logoUrl ? (
                <img src={company.logoUrl} alt={company.name} className="w-full h-full object-contain p-4" />
              ) : (
                <>
                  <div className="w-16 h-16 flex items-center justify-center mb-2">
                    <i className={`${company.logoIcon} text-white text-6xl`} />
                  </div>
                  <span className="text-white font-black text-4xl tracking-wider">{company.logoInitials}</span>
                </>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 text-center lg:text-left">
            <div className="flex flex-wrap items-center gap-3 mb-3 justify-center lg:justify-start">
              <span className="bg-white/15 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                🇰🇷 {isKr ? "한국 기업" : "Doanh nghiệp Hàn Quốc"}
              </span>
              {(company.mainCategoryName || company.mainCategoryNameKr) && (
                <span className="bg-white/15 text-white text-xs px-3 py-1 rounded-full whitespace-nowrap">
                  {isKr ? (company.mainCategoryNameKr || company.mainCategoryName) : (company.mainCategoryName || company.mainCategoryNameKr)}
                </span>
              )}
            </div>
            <h1 className="text-3xl lg:text-5xl font-black text-white leading-tight mb-3">{company.name}</h1>
            {company.nameKr && isKr && (
              <p className="text-white/60 text-base font-medium mb-2">{company.nameKr}</p>
            )}
            {(isKr ? company.taglineKr : company.taglineVi) && (
              <p className="text-white/80 text-lg font-medium mb-6 max-w-2xl">
                {isKr ? company.taglineKr : company.taglineVi}
              </p>
            )}

            <div className="flex flex-wrap gap-6 justify-center lg:justify-start">
              {company.founded && (
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-line text-white/80 text-sm" />
                  </div>
                  <div>
                    <div className="text-white/50 text-[10px] uppercase tracking-wide">{isKr ? "설립" : "Thành lập"}</div>
                    <div className="text-white font-semibold text-sm">{company.founded}</div>
                  </div>
                </div>
              )}
              {company.location && (
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-white/80 text-sm" />
                  </div>
                  <div>
                    <div className="text-white/50 text-[10px] uppercase tracking-wide">{isKr ? "위치" : "Địa điểm"}</div>
                    <div className="text-white font-semibold text-sm">{company.location}</div>
                  </div>
                </div>
              )}
              {company.employees && (
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-group-line text-white/80 text-sm" />
                  </div>
                  <div>
                    <div className="text-white/50 text-[10px] uppercase tracking-wide">{isKr ? "직원" : "Nhân viên"}</div>
                    <div className="text-white font-semibold text-sm">{company.employees}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* CTA - Website lên trên cùng (màu trắng), Xem sản phẩm xuống thứ hai */}
          <div className="flex flex-col gap-3 flex-shrink-0">
            {company.website && (
              <a href={company.website} target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-2 bg-white text-gray-900 hover:bg-gray-100 px-6 py-3 rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-external-link-line" />
                {isKr ? "공식 사이트" : "Website Chính Thức"}
              </a>
            )}
            <Link to="/danh-muc" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-search-line" />
              {isKr ? "상품 보기" : "Xem Sản Phẩm"}
            </Link>
            {company.extraLinks && company.extraLinks.slice(0, 2).map((link, i) => (
              <a key={i} href={link.url} target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-links-line" />
                {link.label || (isKr ? "링크" : "Liên kết")}
              </a>
            ))}
            <a href="https://zalo.me/0965180170" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-mail-send-line" />
              {isKr ? "문의하기" : "Liên Hệ"}
            </a>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-[#F8F9FA] to-transparent" />
    </section>
  );
};

// ── Products Grid ─────────────────────────────────────────────────────────────
const CompanyProductCard = ({ product, index, colorFrom }: { product: Product; index: number; colorFrom: string }) => {
  const { ref, isVisible } = useScrollReveal(0.1);

  return (
    <Link
      ref={ref}
      to={getProductUrl({ companyName: product.companyName ?? "", name: product.name })}
      className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-[#004A99]/20 transition-all duration-700 cursor-pointer block"
      style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateY(0)" : "translateY(40px)", transitionDelay: `${index * 100}ms` }}
    >
      <div className="h-48 overflow-hidden bg-gray-50">
        {product.images[0] ? (
          <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: `${colorFrom}10` }}>
            <i className="ri-image-line text-4xl" style={{ color: colorFrom }} />
          </div>
        )}
      </div>
      <div className="p-5">
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full mb-2 inline-block whitespace-nowrap" style={{ backgroundColor: `${colorFrom}12`, color: colorFrom }}>
          {product.subCategoryName}
        </span>
        <h3 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2 mb-2 hover:text-[#004A99] transition-colors">{product.name}</h3>
        <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{product.description}</p>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {product.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${colorFrom}10`, color: colorFrom }}>
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
};

// ── Other Companies ───────────────────────────────────────────────────────────
const OtherCompanies = ({ currentId, currentCategoryId, currentCategoryName, currentCategoryNameKr, isKr }: { currentId: string; currentCategoryId: string; currentCategoryName: string; currentCategoryNameKr: string; isKr: boolean }) => {
  const { companies } = useCompanies();
  // Filter: same category (by ID if available, fallback to name), exclude self
  const others = companies.filter((c) => {
    if (c.id === currentId) return false;
    if (!currentCategoryId && !currentCategoryName) return true;
    // Primary: match by mainCategoryId
    if (c.mainCategoryId && currentCategoryId) return c.mainCategoryId === currentCategoryId;
    // Fallback: match by category name
    return c.mainCategoryName === currentCategoryName;
  }).slice(0, 10);
  if (others.length === 0) return null;
  const displayCategoryName = currentCategoryName || "Cùng ngành";
  const displayCategoryNameKr = currentCategoryNameKr || currentCategoryName || "같은 분야";
  const sectionTitle = isKr
    ? `다른 한국 기업 - ${displayCategoryNameKr}`
    : `Doanh Nghiệp Hàn Quốc Khác - ${displayCategoryName}`;

  return (
    <section className="py-16 bg-[#F8F9FA] border-t border-gray-100">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <h3 className="text-xl font-bold text-[#212529]">
            {sectionTitle}
          </h3>
          <Link
            to="/doanh-nghiep"
            className="flex items-center gap-1.5 text-sm text-[#004A99] font-semibold hover:underline cursor-pointer whitespace-nowrap"
          >
            {isKr ? "전체 기업 보기" : "Xem tất cả doanh nghiệp"}
            <i className="ri-arrow-right-line text-sm" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {others.map((c) => (
            <Link
              key={c.id}
              to={`/doanh-nghiep/${c.id}`}
              className="flex flex-col items-center p-4 bg-white rounded-2xl hover:shadow-sm transition-all cursor-pointer group"
            >
              {/* Logo - no border box, increased ~70% */}
              <div
                className="w-32 h-32 rounded-2xl flex flex-col items-center justify-center mb-3 flex-shrink-0 overflow-hidden"
                style={
                  c.logoUrl
                    ? { background: "#fff" }
                    : { background: `linear-gradient(135deg, ${c.logoFrom ?? "#1C4E6B"}, ${c.logoTo ?? "#2B7A9C"})` }
                }
              >
                {c.logoUrl ? (
                  <img src={c.logoUrl} alt={c.name} className="w-full h-full object-contain p-2" />
                ) : (
                  <>
                    <i className={`${c.logoIcon} text-white text-3xl`} />
                    {c.logoInitials && (
                      <span className="text-white font-black text-sm mt-0.5">{c.logoInitials}</span>
                    )}
                  </>
                )}
              </div>
              <p className="text-xs font-semibold text-[#212529] text-center line-clamp-2 group-hover:text-[#004A99] transition-colors leading-tight">
                {c.name}
              </p>
              {(c.mainCategoryName || c.mainCategoryNameKr) && (
                <span className="mt-1.5 text-[10px] text-gray-400 text-center line-clamp-1 px-2 py-0.5 bg-gray-50 rounded-full">
                  {isKr ? (c.mainCategoryNameKr || c.mainCategoryName) : (c.mainCategoryName || c.mainCategoryNameKr)}
                </span>
              )}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Company Info Box ──────────────────────────────────────────────────────────
const CompanyInfoBox = ({ company, isKr }: { company: CompanyBasic; isKr: boolean }) => {
  const { ref, isVisible } = useScrollReveal(0.1);
  const colorFrom = company.logoFrom ?? "#004A99";
  const colorTo = company.logoTo ?? colorFrom;

  return (
    <div ref={ref} className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-1 h-8 rounded-full" style={{ background: `linear-gradient(to bottom, ${colorFrom}, ${colorTo})` }} />
        <span className="text-xs font-bold uppercase tracking-widest" style={{ color: colorFrom }}>
          {isKr ? "기업 정보" : "Thông Tin Doanh Nghiệp"}
        </span>
      </div>
      <h2 className="text-3xl font-black text-[#212529] mb-8">{isKr ? "기업 소개" : "Giới Thiệu Công Ty"}</h2>

      <div className="rounded-2xl p-8 border" style={{ background: `linear-gradient(135deg, ${colorFrom}08, ${colorTo}05)`, borderColor: `${colorFrom}20` }}>
        <div className="flex flex-col lg:flex-row items-start gap-6">
          {/* Logo */}
          <div
            className="w-28 h-28 rounded-2xl flex flex-col items-center justify-center flex-shrink-0 overflow-hidden"
            style={company.logoUrl ? { background: "#fff", border: `1px solid ${colorFrom}20` } : { background: `linear-gradient(135deg, ${colorFrom}, ${colorTo})` }}
          >
            {company.logoUrl ? (
              <img src={company.logoUrl} alt={company.name} className="w-full h-full object-contain p-2" />
            ) : (
              <>
                <i className={`${company.logoIcon} text-white text-3xl`} />
                <span className="text-white font-black text-base mt-1">{company.logoInitials}</span>
              </>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-black text-[#212529] mb-1">{company.name}</h3>
            {company.nameKr && <p className="text-gray-500 text-sm mb-3">{company.nameKr}</p>}

            <div className="flex flex-wrap gap-4 mb-4">
              {company.location && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <i className="ri-map-pin-line text-gray-400" />{company.location}
                </div>
              )}
              {company.founded && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <i className="ri-calendar-line text-gray-400" />{isKr ? "설립" : "Thành lập"} {company.founded}
                </div>
              )}
              {company.employees && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <i className="ri-group-line text-gray-400" />{company.employees} {isKr ? "임직원" : "nhân viên"}
                </div>
              )}
              {(company.mainCategoryName || company.mainCategoryNameKr) && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <i className="ri-price-tag-3-line text-gray-400" />
                  {isKr ? (company.mainCategoryNameKr || company.mainCategoryName) : (company.mainCategoryName || company.mainCategoryNameKr)}
                </div>
              )}
            </div>

            {/* Description */}
            {(isKr ? company.descriptionKr : company.descriptionVi) && (
              <div
                className="text-gray-600 text-sm leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1 [&_a]:text-[#004A99] [&_a]:underline [&_strong]:font-bold mb-4"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    isKr ? (company.descriptionKr ?? "") : (company.descriptionVi ?? ""),
                    {
                      ADD_ATTR: ["target", "rel"],
                      ALLOWED_TAGS: ["p","br","strong","b","em","i","u","a","ul","ol","li","span","h1","h2","h3","h4"],
                      ALLOWED_ATTR: ["href","target","rel","class","style"],
                    }
                  )
                }}
              />
            )}

            {/* Extra links */}
            {company.extraLinks && company.extraLinks.filter((l) => l.url).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {company.extraLinks.filter((l) => l.url).map((link, i) => (
                  <a
                    key={i}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs text-[#004A99] hover:border-[#004A99]/50 hover:bg-[#004A99]/5 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-external-link-line text-[10px]" />
                    {link.label || link.url}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* CTA */}
          <div className="flex-shrink-0">
            <a href="https://zalo.me/0965180170" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-white px-6 py-3 rounded-xl font-semibold text-sm cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90" style={{ background: `linear-gradient(135deg, ${colorFrom}, ${colorTo})` }}>
              <i className="ri-mail-line" />
              {isKr ? "파트너 문의" : "Yêu Cầu Hợp Tác"}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Company Gallery + Certifications (merged) ─────────────────────────────────
const CompanyMediaSection = ({
  images,
  certs,
  isKr,
  colorFrom,
}: {
  images: string[];
  certs: { title?: string; image?: string; image_url?: string; description?: string }[];
  isKr: boolean;
  colorFrom: string;
}) => {
  const { ref, isVisible } = useScrollReveal(0.1);
  const hasImages = images && images.length > 0;
  const hasCerts = certs && certs.length > 0;
  if (!hasImages && !hasCerts) return null;

  return (
    <section className="py-16 bg-[#F8F9FA]">
      <div className="max-w-[1400px] mx-auto px-6">
        <div ref={ref} className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1 h-8 rounded-full" style={{ background: `linear-gradient(to bottom, ${colorFrom}, #00AEEF)` }} />
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: colorFrom }}>
              {isKr ? "기업 미디어" : "Hình Ảnh Doanh Nghiệp"}
            </p>
          </div>
          <h2 className="text-3xl font-black text-[#212529] mb-8">
            {isKr ? "갤러리 & 인증서" : "Hình Ảnh Doanh Nghiệp"}
          </h2>

          {/* Gallery images */}
          {hasImages && (
            <div className="mb-10">
              <h3 className="text-base font-bold text-gray-700 mb-4">{isKr ? "갤러리" : "Thư viện ảnh"}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {images.map((img, i) => (
                  <div key={i} className="rounded-2xl overflow-hidden bg-gray-100 aspect-video">
                    <img src={img} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Certifications */}
          {hasCerts && (
            <div>
              <h3 className="text-base font-bold text-gray-700 mb-4">{isKr ? "인증 및 특허" : "Chứng nhận & Bằng sáng chế"}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {certs.map((cert, i) => {
                  const imgSrc = cert.image_url || cert.image;
                  return (
                    <div key={i} className="bg-white rounded-2xl p-5 border border-gray-100 flex flex-col items-center text-center">
                      {imgSrc && (
                        <div className="w-full h-36 rounded-xl overflow-hidden mb-4 bg-[#F8F9FA]">
                          <img src={imgSrc} alt={cert.title ?? ""} className="w-full h-full object-contain p-2" />
                        </div>
                      )}
                      {cert.title && <p className="text-sm font-bold text-gray-900 mb-1">{cert.title}</p>}
                      {cert.description && <p className="text-xs text-gray-500 leading-relaxed">{cert.description}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const CompanyDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { i18n } = useTranslation();
  const isKr = i18n.language === "kr";
  const { company, loading } = useCompany(id);
  const { products, loading: productsLoading } = useProducts();

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [id]);

  const companyProducts = products.filter(
    (p) => p.companyName === company?.name || p.companyCode === id
  );
  const colorFrom = company?.logoFrom ?? "#004A99";

  if (loading) return <CompanySkeleton />;

  if (!company) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Navbar />
        <main className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
          <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-6">
            <i className="ri-building-2-line text-4xl text-gray-400" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold text-[#212529] mb-3">{isKr ? "기업을 찾을 수 없습니다" : "Không tìm thấy doanh nghiệp"}</h1>
          <p className="text-gray-500 mb-8">{isKr ? "요청한 기업 정보가 존재하지 않습니다." : "Thông tin doanh nghiệp không tồn tại."}</p>
          <Link to="/doanh-nghiep" className="bg-[#004A99] text-white px-6 py-3 rounded-xl font-semibold text-sm cursor-pointer whitespace-nowrap">
            {isKr ? "기업 목록 보기" : "Xem Danh Sách Doanh Nghiệp"}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const siteUrl = typeof window !== "undefined" ? `${window.location.protocol}//${window.location.host}` : "";

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <SEO
        title={`${company.name} – Doanh nghiệp Hàn Quốc | KCANIA`}
        description={`${company.taglineVi || `Thông tin doanh nghiệp ${company.name} từ Hàn Quốc.`} Khám phá sản phẩm và hợp tác với ${company.name} qua nền tảng KCANIA.`}
        keywords={`${company.name}, ${company.mainCategoryName}, doanh nghiệp Hàn Quốc, ${company.name} sản phẩm, KCANIA`}
        ogImage={company.logoUrl || company.images?.[0]}
        ogType="profile"
      />
      <LocalBusinessSchema
        name={company.name}
        description={company.descriptionVi || company.taglineVi}
        url={company.website}
        telephone={undefined}
        address={{
          addressLocality: company.location,
          addressCountry: "KR",
        }}
        image={company.logoUrl}
        logo={company.logoUrl}
        sameAs={[...(company.website ? [company.website] : []), ...(company.extraLinks?.map((l) => l.url).filter(Boolean) ?? [])]}
      />
      <BreadcrumbSchema
        items={[
          { name: "Trang chủ", url: siteUrl || "/" },
          { name: "Giới Thiệu Doanh Nghiệp", url: `${siteUrl}/doanh-nghiep` },
          { name: company.name, url: window.location.href },
        ]}
      />

      <Navbar />

      <main>
        <CompanyDetailHero company={company} isKr={isKr} />

        <section className="py-16 bg-white" aria-label="Thông tin doanh nghiệp">
          <div className="max-w-[1400px] mx-auto px-6">
            <CompanyInfoBox company={company} isKr={isKr} />
          </div>
        </section>

        <CompanyMediaSection
          images={company.images ?? []}
          certs={company.patentsCertifications ?? []}
          isKr={isKr}
          colorFrom={colorFrom}
        />

        <OtherCompanies currentId={company.id} currentCategoryId={company.mainCategoryId} currentCategoryName={company.mainCategoryName} currentCategoryNameKr={company.mainCategoryNameKr} isKr={isKr} />

        {/* Internal Linking */}
        <nav aria-label="Liên kết liên quan" className="bg-white border-t border-gray-100 py-6">
          <div className="max-w-[1400px] mx-auto px-6">
            <p className="text-xs text-gray-400 mb-3 font-semibold uppercase tracking-wider">Khám phá thêm</p>
            <div className="flex flex-wrap gap-2">
              <Link to="/doanh-nghiep" className="text-xs bg-[#F8F9FA] border border-gray-200 hover:border-[#004A99]/30 text-gray-600 hover:text-[#004A99] px-3 py-1.5 rounded-lg transition-colors">
                Tất cả doanh nghiệp Hàn Quốc
              </Link>
              <Link to="/danh-muc" className="text-xs bg-[#F8F9FA] border border-gray-200 hover:border-[#004A99]/30 text-gray-600 hover:text-[#004A99] px-3 py-1.5 rounded-lg transition-colors">
                Danh mục sản phẩm
              </Link>
              {company.mainCategoryName && (
                <Link to="/danh-muc" className="text-xs bg-[#F8F9FA] border border-gray-200 hover:border-[#004A99]/30 text-gray-600 hover:text-[#004A99] px-3 py-1.5 rounded-lg transition-colors">
                  {company.mainCategoryName}
                </Link>
              )}
              <Link to="/hop-tac-ky-thuat" className="text-xs bg-[#F8F9FA] border border-gray-200 hover:border-[#004A99]/30 text-gray-600 hover:text-[#004A99] px-3 py-1.5 rounded-lg transition-colors">
                Hợp tác kỹ thuật
              </Link>
            </div>
          </div>
        </nav>
      </main>
      <Footer />
    </div>
  );
};

export default CompanyDetailPage;
